import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import WebViewRexnord from './WebViewRexnord';
import WebView from './WebView';
import ProtectedRoute from './ProtectedRoute';
import { KioskWebViewAccessGate } from './KioskWebViewAccessGate';
import { buildWebviewFetchHeaders, isWebViewAuthBypass, parseCameraIdsFromSearch } from '../utils/webviewAudio';
import { canAccessKioskWebview, canAccessKioskIntrusionWebview, isKioskMode } from '../utils/kioskMode';
import WebViewIntrusion from './WebViewIntrusion';

// API base URL helper
const normalizeBaseUrl = (value) => {
  if (!value) return '';
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const resolveApiBase = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    const origin = window.location.origin;
    const url = new URL(origin);
    if (url.protocol === 'https:') {
      return normalizeBaseUrl(origin);
    }
    return normalizeBaseUrl(`${url.protocol}//${url.hostname}:8000`);
  }
  return normalizeBaseUrl('http://localhost:8000');
};

const API_BASE_URL = resolveApiBase();

// Helper to check if company is Rexnord
const isRexnordCompany = (companyName) => {
  if (!companyName) return false;
  return companyName.toLowerCase().includes('rexnord');
};

const parseLaneFromPath = (pathname = '') => {
  const parts = pathname.split('/').filter(Boolean);
  const webviewIdx = parts.findIndex((p) => p.toLowerCase() === 'webview');
  if (webviewIdx < 0 || webviewIdx + 1 >= parts.length) return null;
  const rawPart = String(parts[webviewIdx + 1] || '');
  let decoded = rawPart;
  try {
    decoded = decodeURIComponent(rawPart);
  } catch (e) {
    decoded = rawPart;
  }
  const raw = decoded.toLowerCase().trim();
  if (!raw) return null;
  const compact = raw.replace(/\s+/g, '');
  const match =
    compact.match(/^lane(\d+)$/) ||
    compact.match(/^lane-(\d+)$/) ||
    compact.match(/^(\d+)$/);
  if (!match) return null;
  const lane = parseInt(match[1], 10);
  return Number.isNaN(lane) || lane <= 0 ? null : lane;
};

const WebViewRouter = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [isRexnord, setIsRexnord] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const location = useLocation();
  const laneNumber = parseLaneFromPath(location?.pathname || '');
  const cameraIds = parseCameraIdsFromSearch(location?.search || '');

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const apiUrl = `${API_BASE_URL}/api/v1/settings/company`;
        console.log('[WebViewRouter] Fetching company settings from:', apiUrl);
        
        const response = await fetch(apiUrl, { headers: buildWebviewFetchHeaders() });
        
        if (!response.ok) {
          console.error('[WebViewRouter] Failed to fetch company settings. Status:', response.status);
          // Default to WebViewDefault on error
          setIsRexnord(false);
          setLoading(false);
          return;
        }
        
        const data = await response.json();
        console.log('[WebViewRouter] Fetched company settings:', data);
        
        // Check if company name includes 'rexnord' (case-insensitive)
        // API returns company_name field
        const companyName = data.company_name || data.name || '';
        const isRexnordResult = isRexnordCompany(companyName);
        
        console.log('[WebViewRouter] Company name:', companyName);
        console.log('[WebViewRouter] Is Rexnord:', isRexnordResult);
        
        setIsRexnord(isRexnordResult);
        setLoading(false);
      } catch (err) {
        console.error('[WebViewRouter] Error fetching company settings:', err);
        setError(err.message);
        // Default to WebViewDefault on error
        setIsRexnord(false);
        setLoading(false);
      }
    };

    fetchCompanySettings();
  }, []);

  // Show loading spinner while fetching company info
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          '@supports (height: 100dvh)': {
            minHeight: '100dvh',
          },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        }}
      >
        <CircularProgress 
          size={60} 
          sx={{ color: '#40518A', mb: 2 }} 
        />
        <Typography 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            fontSize: '1.2rem',
            fontWeight: 500 
          }}
        >
          Loading...
        </Typography>
      </Box>
    );
  }

  // Render appropriate component based on company
  if (isRexnord) {
    console.log('[WebViewRouter] Rendering WebView (Rexnord)');
    return <WebViewRexnord laneNumber={laneNumber} cameraIds={cameraIds} embedded={embedded} />;
  } else {
    console.log('[WebViewRouter] Rendering WebViewDefault (Other/Default)');
    return <WebView laneNumber={laneNumber} cameraIds={cameraIds} embedded={embedded} />;
  }
};

/** Attendance webview: localhost / REACT_APP_WEBVIEW_AUTH_BYPASS skips admin login. */
export function WebViewRouteGuard() {
  if (isWebViewAuthBypass()) {
    return <WebViewRouter />;
  }
  if (isKioskMode()) {
    return (
      <KioskWebViewAccessGate canAccess={canAccessKioskWebview}>
        <WebViewRouter />
      </KioskWebViewAccessGate>
    );
  }
  return (
    <ProtectedRoute allowedRoles={['admin']}>
      <WebViewRouter />
    </ProtectedRoute>
  );
}

export function CombinedWebView() {
  return (
    <Box
      sx={{
        height: '100vh',
        '@supports (height: 100dvh)': { height: '100dvh' },
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'minmax(0, 4fr) minmax(0, 6fr)',
        },
        gridTemplateRows: {
          xs: 'minmax(280px, 1fr) minmax(280px, 1fr)',
          md: '1fr',
        },
        gap: { xs: 0.75, md: 1 },
        p: { xs: 0.75, md: 1 },
        overflow: { xs: 'auto', md: 'hidden' },
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      }}
    >
      <Box
        sx={{
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          borderRadius: { xs: 1.5, md: 2 },
        }}
      >
        <WebViewRouter embedded />
      </Box>
      <Box
        sx={{
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          borderRadius: { xs: 1.5, md: 2 },
        }}
      >
        <WebViewIntrusion embedded />
      </Box>
    </Box>
  );
}

const canAccessCombinedKiosk = (permissions) =>
  canAccessKioskWebview(permissions) && canAccessKioskIntrusionWebview(permissions);

export function CombinedWebViewRouteGuard() {
  if (isWebViewAuthBypass()) {
    return <CombinedWebView />;
  }
  if (isKioskMode()) {
    return (
      <KioskWebViewAccessGate canAccess={canAccessCombinedKiosk}>
        <CombinedWebView />
      </KioskWebViewAccessGate>
    );
  }
  return (
    <ProtectedRoute allowedRoles={['admin', 'intrusion']}>
      <CombinedWebView />
    </ProtectedRoute>
  );
}

export default WebViewRouter;
